import React, { useEffect, useState } from "react";
import { api, fmtINR, CATEGORY_LABELS } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, XAxis, YAxis, Tooltip, CartesianGrid, ResponsiveContainer, Legend } from "recharts";

const CHART_COLORS = ["#134E3F", "#E27D60", "#475569", "#F59E0B", "#3B82F6"];
const TOOLTIP_STYLE = { borderRadius: 12, border: "1px solid #E2E0D8" };

export default function Reports() {
  const [trend, setTrend] = useState([]);
  const [pins, setPins] = useState([]);
  const [overview, setOverview] = useState(null);
  const [members, setMembers] = useState([]);

  useEffect(() => {
    (async () => {
      const [t, tp, o, m] = await Promise.all([
        api.get("/stats/trend"),
        api.get("/stats/top-pins"),
        api.get("/stats/overview"),
        api.get("/members"),
      ]);
      setTrend(t.data); setPins(tp.data); setOverview(o.data); setMembers(m.data);
    })();
  }, []);

  const categoryDist = Object.entries(CATEGORY_LABELS).map(([k, v]) => ({
    name: v,
    value: members.filter((m) => m.category === k).length,
  }));

  return (
    <div className="p-8 space-y-6">
      <header>
        <div className="label-caps text-accent mb-2">Analytics</div>
        <h1 className="font-heading text-4xl tracking-tight text-primary">Reports</h1>
        <p className="text-muted-foreground mt-2 max-w-2xl">Deep-dive views of the checkup program performance across the last two weeks.</p>
      </header>

      {overview && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card className="p-6 border-border"><div className="label-caps text-muted-foreground">Lifetime revenue</div><div className="font-heading text-3xl mt-2 text-primary">{fmtINR(overview.revenue_total)}</div></Card>
          <Card className="p-6 border-border"><div className="label-caps text-muted-foreground">Total orders</div><div className="font-heading text-3xl mt-2 text-primary">{overview.total_orders}</div></Card>
          <Card className="p-6 border-border"><div className="label-caps text-muted-foreground">Members</div><div className="font-heading text-3xl mt-2 text-primary">{overview.total_members}</div></Card>
          <Card className="p-6 border-border"><div className="label-caps text-muted-foreground">Serviceable PINs</div><div className="font-heading text-3xl mt-2 text-primary">{overview.active_pins}</div></Card>
        </div>
      )}

      <Card className="p-6 border-border">
        <div className="label-caps text-muted-foreground mb-2">14-day performance</div>
        <h3 className="font-heading text-2xl text-foreground mb-4">Orders vs Enrollments vs Revenue</h3>
        <ResponsiveContainer width="100%" height={320}>
          <LineChart data={trend}>
            <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8" />
            <XAxis dataKey="date" stroke="#64748B" fontSize={12} />
            <YAxis yAxisId="left" stroke="#64748B" fontSize={12} />
            <YAxis yAxisId="right" orientation="right" stroke="#E27D60" fontSize={12} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="orders" stroke="#134E3F" strokeWidth={2.5} />
            <Line yAxisId="left" type="monotone" dataKey="enrollments" stroke="#475569" strokeWidth={2.5} />
            <Line yAxisId="right" type="monotone" dataKey="revenue" stroke="#E27D60" strokeWidth={2.5} />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-6 border-border">
          <div className="label-caps text-muted-foreground mb-2">By category</div>
          <h3 className="font-heading text-xl text-foreground mb-4">Member mix</h3>
          <ResponsiveContainer width="100%" height={240}>
            <PieChart>
              <Pie data={categoryDist} dataKey="value" nameKey="name" outerRadius={90} paddingAngle={3}>
                {categoryDist.map((entry, i) => <Cell key={entry.name} fill={CHART_COLORS[i % CHART_COLORS.length]}/>)}
              </Pie>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 border-border">
          <div className="label-caps text-muted-foreground mb-2">Demand geography</div>
          <h3 className="font-heading text-xl text-foreground mb-4">Top PINs</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={pins}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E0D8"/>
              <XAxis dataKey="pin" stroke="#64748B" fontSize={12}/>
              <YAxis stroke="#64748B" fontSize={12}/>
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Bar dataKey="count" fill="#134E3F" radius={[6, 6, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
}
